import { useTranslation } from 'react-i18next';

/**
 * "Our team" — renders ONLY when real photos exist in src/assets/team/.
 *
 * Zero-config for the owner: drop image files into Apps/web/src/assets/team/
 * (e.g. `Elyas-Binothman.webp`) and the section appears; the filename (minus
 * extension, dashes → spaces) is shown as the person's name. No stock people,
 * ever — an empty folder simply hides the whole section.
 *
 * NOTE .gitignore: src/assets/team/* is whitelisted — new photos WILL commit.
 */
const teamImages = import.meta.glob('../../assets/team/*.{png,jpg,jpeg,webp}', {
  eager: true,
  query: '?url',
  import: 'default',
}) as Record<string, string>;

const members = Object.entries(teamImages)
  .sort(([a], [b]) => a.localeCompare(b))
  .map(([path, url]) => {
    const file = path.split('/').pop() ?? '';
    const name = file.replace(/\.[a-z]+$/i, '').replace(/[-_]+/g, ' ').trim();
    return { name, url };
  });

export default function TeamSection() {
  const { i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';

  if (members.length === 0) return null;

  return (
    <section className="border-t border-slate-200 bg-white py-16 sm:py-20">
      <div className="mx-auto max-w-5xl px-6">
        <h2 className="mb-2 text-center text-3xl font-bold text-slate-900 sm:text-4xl">
          {isRTL ? 'فريقنا' : 'Our team'}
        </h2>
        <p className="mx-auto mb-10 max-w-xl text-center text-slate-600">
          {isRTL ? 'الأشخاص الذين يقفون خلف صفقات 24.' : 'The people behind Safaqat 24.'}
        </p>
        <div className="flex flex-wrap justify-center gap-8">
          {members.map((member) => (
            <figure key={member.url} className="flex w-40 flex-col items-center text-center">
              <img
                src={member.url}
                alt={member.name}
                width={160}
                height={160}
                loading="lazy"
                decoding="async"
                className="h-32 w-32 rounded-2xl border border-[var(--pv-border-soft)] object-cover shadow-sm"
              />
              <figcaption className="mt-3 text-sm font-semibold text-slate-800">{member.name}</figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}
